const BASE = '/manage' as const

export const adminRoutes = {
  root: BASE,

  people: {
    root: `${BASE}/people`,
    members: `${BASE}/people/members`,
    membersExport: (exportId: string) =>
      `${BASE}/people/members/export/${exportId}`,
    staff: `${BASE}/people/staff`,
    invitations: `${BASE}/people/invitations`,
    fields: `${BASE}/people/fields`,
    badges: `${BASE}/people/badges`,
  },

  content: {
    root: `${BASE}/content`,
    posts: `${BASE}/content/posts`,
    events: `${BASE}/content/events`,
    spaces: `${BASE}/content/spaces`,
    tags: `${BASE}/content/tags`,
    cms: {
      root: `${BASE}/content/cms`,
      detail: (slug: string) => `${BASE}/content/cms/${slug}`,
    },
  },

  site: {
    root: `${BASE}/site`,
    spaces: {
      root: (id: string) => `${BASE}/site/spaces/${id}`,
      general: (id: string) => `${BASE}/site/spaces/${id}/general`,
      permission: (id: string) => `${BASE}/site/spaces/${id}/permission`,
      members: (id: string) => `${BASE}/site/spaces/${id}/members`,
      seo: (id: string) => `${BASE}/site/spaces/${id}/seo`,
      dangerZone: (id: string) => `${BASE}/site/spaces/${id}/danger-zone`,
    },
    cms: {
      new: `${BASE}/site/cms/new`,
      detail: (slug: string) => `${BASE}/site/cms/${slug}`,
      settings: (slug: string) => `${BASE}/site/cms/${slug}/settings`,
    },
  },

  settings: {
    root: `${BASE}/settings`,
    network: {
      root: `${BASE}/settings/network`,
      general: `${BASE}/settings/network/general`,
      publish: `${BASE}/settings/network/publish`,
      permissions: `${BASE}/settings/network/permissions`,
      homepage: `${BASE}/settings/network/homepage`,
      defaultSpaces: `${BASE}/settings/network/default-spaces`,
      media: `${BASE}/settings/network/media`,
      whiteLabel: `${BASE}/settings/network/white-label`,
      other: `${BASE}/settings/network/other`,
    },
    authentication: {
      root: `${BASE}/settings/authentication`,
      email: `${BASE}/settings/authentication/email`,
      social: `${BASE}/settings/authentication/social`,
      oauth2: `${BASE}/settings/authentication/oauth2`,
      jwt: `${BASE}/settings/authentication/jwt`,
      saml: `${BASE}/settings/authentication/saml`,
    },
    domain: `${BASE}/settings/domain`,
    search: {
      root: `${BASE}/settings/search`,
      searchSources: `${BASE}/settings/search/search-sources`,
      officialResources: `${BASE}/settings/search/official-resources`,
      askAi: `${BASE}/settings/search/ask-ai`,
      federatedSearch: `${BASE}/settings/search/federated-search`,
    },
    messaging: {
      root: `${BASE}/settings/messaging`,
      global: `${BASE}/settings/messaging/global`,
      members: `${BASE}/settings/messaging/members`,
      groups: `${BASE}/settings/messaging/groups`,
    },
    moderation: {
      root: `${BASE}/settings/moderation`,
      aiSpam: `${BASE}/settings/moderation/ai-spam`,
      alerts: `${BASE}/settings/moderation/alerts`,
      monitoredContents: `${BASE}/settings/moderation/monitored-contents`,
      ageBased: `${BASE}/settings/moderation/age-based`,
      flaggedMembers: `${BASE}/settings/moderation/flagged-members`,
    },
    localization: {
      root: `${BASE}/settings/localization`,
      communityLanguage: `${BASE}/settings/localization/community-language`,
      postTranslation: `${BASE}/settings/localization/post-translation`,
    },
    notifications: {
      root: `${BASE}/settings/notifications`,
      emailNotifications: `${BASE}/settings/notifications/email-notifications`,
      emailDigest: `${BASE}/settings/notifications/email-digest`,
      fromEmail: `${BASE}/settings/notifications/from-email`,
    },
    seo: {
      root: `${BASE}/settings/seo`,
      seo: `${BASE}/settings/seo/seo`,
      preview: `${BASE}/settings/seo/preview`,
    },
    security: {
      root: `${BASE}/settings/security`,
      passwordStrength: `${BASE}/settings/security/password-strength`,
      captcha: `${BASE}/settings/security/captcha`,
      embed: `${BASE}/settings/security/embed`,
      privacy: `${BASE}/settings/security/privacy`,
    },
  },

  design: {
    root: `${BASE}/design`,
    createSpace: (templateId?: string) =>
      templateId
        ? `${BASE}/design/create-space/${templateId}`
        : `${BASE}/design/create-space`,
    createSpaceInCollection: (collectionId: string) =>
      `${BASE}/design/create-space?collectionId=${collectionId}`,
    customizer: {
      root: `${BASE}/design/customizer`,
      navigation: `${BASE}/design/customizer/navigation`,
      spaceBlocks: (spaceId: string) =>
        `${BASE}/design/customizer/spaces/${spaceId}/blocks`,
      cmsBlocks: (postTypeId: string) =>
        `${BASE}/design/customizer/cms/${postTypeId}/blocks`,
    },
    appearance: {
      root: `${BASE}/design/appearance`,
      logos: `${BASE}/design/appearance/logos`,
      colors: `${BASE}/design/appearance/colors`,
      typography: `${BASE}/design/appearance/typography`,
      styles: `${BASE}/design/appearance/styles`,
      navigation: `${BASE}/design/appearance/navigation`,
    },
  },

  billing: {
    root: `${BASE}/billing`,
    plans: `${BASE}/billing/plans`,
    usages: `${BASE}/billing/usages`,
  },

  reports: {
    root: `${BASE}/reports`,
    analytics: {
      root: `${BASE}/reports/analytics`,
      reach: `${BASE}/reports/analytics/reach`,
      people: `${BASE}/reports/analytics/people`,
      posts: `${BASE}/reports/analytics/posts`,
      events: `${BASE}/reports/analytics/events`,
      spaces: `${BASE}/reports/analytics/spaces`,
      messages: `${BASE}/reports/analytics/messages`,
    },
    auditLogs: `${BASE}/reports/audit-logs`,
    emailLogs: `${BASE}/reports/email-logs`,
  },

  appStore: {
    root: `${BASE}/app-store`,
    integrations: `${BASE}/app-store/integrations`,
    addons: {
      root: `${BASE}/app-store/addons`,
      detail: (addonId: string) => `${BASE}/app-store/addons/${addonId}`,
      byCategory: (category: string) =>
        `${BASE}/app-store/addons?category=${category}`,
    },
    apps: {
      root: `${BASE}/app-store/integrations`,
      detail: (appId: string) => `${BASE}/app-store/apps/${appId}`,
    },
    byPlan: (planOrder: number | string) =>
      `${BASE}/app-store/by-plan?planOrder=${planOrder}`,
  },

  customizer: {
    templates: {
      root: `${BASE}/customizer/templates`,
      new: `${BASE}/customizer/templates/new`,
      edit: (templateId: string, spaceId?: string) => {
        const base = `${BASE}/customizer/templates/edit/${templateId}/`
        return spaceId ? `${base}?spaceId=${spaceId}` : base
      },
    },
  },

  onboarding: `${BASE}/onboarding`,
} as const
